import React, { useState, useEffect, useCallback } from 'react';
import { Monitor, Plus, X, Loader2, Save, Edit2, Trash2, CheckCircle2, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface Device {
  deviceID: number;
  deviceName: string;
  deviceCode?: string;
  ipAddress: string;
  port: number;
  protocol: string;
  deviceCategory?: string;
  serialNumber?: string;
  model?: string;
  password?: string;
  location?: string;
  isActive: boolean;
  isOnline?: boolean;
  lastSyncTime?: string | null;
}

const fmtDateTime = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString('tr-TR');
};

const MealDevices: React.FC = () => {
  const [rows, setRows] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Device | null>(null);
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState<number | null>(null);
  const [form, setForm] = useState({
    deviceName: '',
    deviceCode: '',
    protocol: 'ZKTeco',
    deviceCategory: 'Meal',
    ipAddress: '',
    port: 4370,
    serialNumber: '',
    model: '',
    password: '0',
    location: '',
    isActive: true,
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r: any = await api.get('/meals/devices');
      const raw = r?.data ?? r;
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setRows(arr);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Cihazlar yüklenemedi');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      deviceName: '', deviceCode: '', protocol: 'ZKTeco', deviceCategory: 'Meal',
      ipAddress: '', port: 4370, serialNumber: '', model: '', password: '0',
      location: '', isActive: true,
    });
    setShowModal(true);
  };
  const openEdit = (d: Device) => {
    setEditing(d);
    setForm({
      deviceName: d.deviceName ?? '',
      deviceCode: d.deviceCode ?? '',
      protocol: d.protocol ?? 'ZKTeco',
      deviceCategory: d.deviceCategory ?? 'Meal',
      ipAddress: d.ipAddress ?? '',
      port: d.port ?? 4370,
      serialNumber: d.serialNumber ?? '',
      model: d.model ?? '',
      password: d.password ?? '0',
      location: d.location ?? '',
      isActive: d.isActive !== false,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.deviceName.trim()) { uyar('Cihaz adı zorunlu'); return; }
    if (!form.ipAddress.trim()) { uyar('IP zorunlu'); return; }
    setSaving(true); setError(null);
    try {
      const body: any = {
        deviceName: form.deviceName,
        deviceCode: form.deviceCode || null,
        protocol: form.protocol,
        deviceCategory: form.deviceCategory || 'Meal',
        ipAddress: form.ipAddress,
        port: Number(form.port) || 4370,
        serialNumber: form.serialNumber || null,
        model: form.model || null,
        password: form.password || '0',
        location: form.location || null,
        isActive: form.isActive,
      };
      if (editing) {
        await api.put(`/meals/devices/${editing.deviceID}`, body);
      } else {
        await api.post('/meals/devices', body);
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const remove = async (d: Device) => {
    if (!(await onayla(`"${d.deviceName}" silinsin mi?`))) return;
    try {
      await api.delete(`/meals/devices/${d.deviceID}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Silinemedi');
    }
  };

  const ping = async (d: Device) => {
    setPinging(d.deviceID);
    try {
      await api.post(`/meals/devices/${d.deviceID}/ping`, {});
      await load();
    } catch { /* ignore, error surface implicit */ }
    finally { setPinging(null); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Monitor size={26} className="text-blue-600" /> Yemekhane Cihazları
          </h1>
          <p className="text-sm text-slate-500 mt-1">Yemekhane geçiş cihazları (ZKTeco / Timmy / Anviz)</p>
        </div>
        <button onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700">
          <Plus size={16} /> Yeni Cihaz
        </button>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Durum</th>
                <th className="px-4 py-3">Ad</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Port</th>
                <th className="px-4 py-3">Protokol</th>
                <th className="px-4 py-3">Konum</th>
                <th className="px-4 py-3">Son Senkron</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                  <Monitor size={32} className="mx-auto opacity-30 mb-2" /> Tanımlı cihaz yok
                </td></tr>
              ) : rows.map(d => (
                <tr key={d.deviceID} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    {d.isOnline ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                        <Wifi size={10} /> online
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">
                        <WifiOff size={10} /> offline
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-800">{d.deviceName}</p>
                    {d.deviceCode && <p className="text-[11px] text-slate-500">{d.deviceCode}</p>}
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-700">{d.ipAddress}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{d.port}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[11px] font-bold">{d.protocol}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{d.location ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(d.lastSyncTime)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => ping(d)} disabled={pinging === d.deviceID}
                      className="mr-2 text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1 text-xs font-bold">
                      {pinging === d.deviceID ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Ping
                    </button>
                    <button onClick={() => openEdit(d)} className="mr-2 text-blue-600 hover:text-blue-800"><Edit2 size={14} /></button>
                    <button onClick={() => remove(d)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-orange-600 text-white px-6 py-4 rounded-t-2xl">
              <h3 className="font-black text-lg">{editing ? 'Cihaz Düzenle' : 'Yeni Yemekhane Cihazı'}</h3>
              <button onClick={() => setShowModal(false)} className="hover:bg-white/20 rounded p-1"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Cihaz Adı *</label>
                  <input type="text" value={form.deviceName} onChange={e => setForm({ ...form, deviceName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Cihaz Kodu</label>
                  <input type="text" value={form.deviceCode} onChange={e => setForm({ ...form, deviceCode: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Protokol *</label>
                  <select value={form.protocol} onChange={e => setForm({ ...form, protocol: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="ZKTeco">ZKTeco</option>
                    <option value="Timmy">Timmy (Anviz OEM)</option>
                    <option value="Anviz">Anviz</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Kategori</label>
                  <select value={form.deviceCategory} onChange={e => setForm({ ...form, deviceCategory: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="Meal">Sadece Yemekhane</option>
                    <option value="Both">Hem PDKS Hem Yemekhane</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-600 mb-1">IP Adresi *</label>
                  <input type="text" value={form.ipAddress} onChange={e => setForm({ ...form, ipAddress: e.target.value })}
                    placeholder="192.168.1.201" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Port</label>
                  <input type="number" value={form.port} onChange={e => setForm({ ...form, port: Number(e.target.value) || 4370 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Seri No</label>
                  <input type="text" value={form.serialNumber} onChange={e => setForm({ ...form, serialNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Model</label>
                  <input type="text" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Şifre</label>
                  <input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Konum</label>
                <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4" />
                Aktif
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 p-4">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50">İptal</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-bold hover:bg-orange-700 disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealDevices;
